import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { Spinner } from "reactstrap";

export function calculateScoreAndLevel(severityCounts = {}, totalCount = 0) {
  const c = severityCounts.critical || 0;
  const h = severityCounts.high || 0;
  const m = severityCounts.medium || 0;
  const l = severityCounts.low || 0;
  const info =
    severityCounts.info !== undefined
      ? severityCounts.info
      : severityCounts.informational || 0;

  let riskLevel = "Low";
  let score = 98.42;

  if (c > 0) {
    riskLevel = "Critical";
    score = Math.max(12, 100 - c * 25 - h * 15 - m * 8).toFixed(2);
  } else if (h > 0) {
    riskLevel = "High";
    score = Math.max(35, 100 - h * 12 - m * 6 - l * 2).toFixed(2);
  } else if (m > 0) {
    riskLevel = "Medium";
    score = Math.max(60, 100 - m * 5 - l * 1.5).toFixed(2);
  } else if (l > 0 || info > 0) {
    riskLevel = "Low";
    score = (98.42).toFixed(2);
  } else {
    riskLevel = "Informational";
    score = (100).toFixed(2);
  }

  score = Number(score);

  const normRisk = riskLevel.toLowerCase();
  const levels = [
    { key: "critical", color: "var(--sev-critical)", op: normRisk === "critical" ? 1 : 0.25 },
    { key: "high", color: "var(--sev-high)", op: normRisk === "high" ? 1 : 0.25 },
    { key: "medium", color: "var(--sev-medium)", op: normRisk === "medium" || normRisk === "med" ? 1 : 0.25 },
    { key: "low", color: "var(--sev-low)", op: normRisk === "low" ? 1 : 0.25 },
    { key: "info", color: "var(--sev-info)", op: normRisk === "info" || normRisk === "informational" ? 1 : 0.25 },
  ];

  const severities = [
    { key: "crit", label: "Critical", count: c, color: "var(--sev-critical)", numColor: c ? "var(--fg-primary)" : "var(--fg-tertiary)" },
    { key: "high", label: "High", count: h, color: "var(--sev-high)", numColor: h ? "var(--fg-primary)" : "var(--fg-tertiary)" },
    { key: "med", label: "Medium", count: m, color: "var(--sev-medium)", numColor: m ? "var(--fg-primary)" : "var(--fg-tertiary)" },
    { key: "low", label: "Low", count: l, color: "var(--sev-low)", numColor: l ? "var(--fg-primary)" : "var(--fg-tertiary)" },
    { key: "info", label: "Informational", count: info, color: "var(--sev-info)", numColor: info ? "var(--fg-primary)" : "var(--fg-tertiary)" },
  ];

  return {
    score,
    riskLevel,
    severities,
    levels,
    trendGrid: [0, 1, 2, 3, 4, 5],
  };
}

// "05 Oct, 14:20" in the viewer's local time
function formatScanTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "short" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} ${month}, ${time}`;
}

const FeatureKpiHeader = ({
  title,
  breadcrumbItem,
  siteTitle,
  score = 98.42,
  riskLabel,
  riskLevel = "Low",
  counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
  levels,
  severities,
  trendGrid,
  trendHistory,
  onRefresh,
  loading = false,
  showTrend = true,
}) => {
  const currentRisk = riskLabel || riskLevel || "Low";
  const normRisk = String(currentRisk).toLowerCase();

  const riskColor =
    normRisk === "critical"
      ? "var(--sev-critical)"
      : normRisk === "high"
      ? "var(--sev-high)"
      : normRisk === "medium" || normRisk === "med"
      ? "var(--sev-medium)"
      : normRisk === "low"
      ? "var(--sev-low)"
      : "var(--sev-info)";

  const levelSegments = useMemo(() => {
    if (Array.isArray(levels) && levels.length > 0) {
      return levels.map((l) => ({
        color: l.color,
        opacity: l.op !== undefined ? l.op : l.opacity !== undefined ? l.opacity : 1,
      }));
    }
    const list = [
      { key: "critical", color: "var(--sev-critical)" },
      { key: "high", color: "var(--sev-high)" },
      { key: "medium", color: "var(--sev-medium)" },
      { key: "low", color: "var(--sev-low)" },
      { key: "info", color: "var(--sev-info)" },
    ];
    return list.map((l) => ({
      color: l.color,
      opacity:
        normRisk === l.key ||
        (normRisk === "med" && l.key === "medium") ||
        (normRisk === "informational" && l.key === "info")
          ? 1
          : 0.25,
    }));
  }, [levels, normRisk]);

  const severitiesList = useMemo(() => {
    if (Array.isArray(severities) && severities.length > 0) {
      return severities;
    }
    return [
      { key: "crit", label: "Critical", count: counts.critical || 0 },
      { key: "high", label: "High", count: counts.high || 0 },
      { key: "med", label: "Medium", count: counts.medium || 0 },
      { key: "low", label: "Low", count: counts.low || 0 },
      {
        key: "info",
        label: "Informational",
        count:
          counts.info !== undefined
            ? counts.info
            : counts.informational || 0,
      },
    ];
  }, [severities, counts]);

  const displayDomain = siteTitle
    ? siteTitle.startsWith("www.")
      ? siteTitle
      : `www.${siteTitle}`
    : "scanme.nmap.org";

  // Current exposure value for display (0% = perfectly clean, 100% = max danger)
  const currentExposure = useMemo(() => {
    const numScore = Number(score) || 98.42;
    return Math.max(0, Number((100 - numScore).toFixed(2)));
  }, [score]);

  // 1. Risk Trend Timeline (last 6 days) from real scan history.
  // Past days come from `trendHistory` (see riskTrend.js); "Today" always uses
  // the live score so it matches the KPI cards. Days with no scan yet → null.
  const timeline = useMemo(() => {
    if (Array.isArray(trendGrid) && trendGrid.length >= 2 && typeof trendGrid[0] === "object") {
      return trendGrid;
    }

    const historyLoading = trendHistory === undefined;
    const byDaysAgo = {};
    (Array.isArray(trendHistory) ? trendHistory : []).forEach((h) => {
      byDaysAgo[h.daysAgo] = h;
    });

    const now = new Date();
    const days = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dayNum = String(d.getDate()).padStart(2, "0");
      const monthStr = d.toLocaleString("en-US", { month: "short" });
      const h = byDaysAgo[i];
      const isToday = i === 0;
      const pointScore = isToday
        ? Number(score) || 98.42
        : h && h.score !== null && h.score !== undefined
        ? Number(h.score)
        : null;

      days.push({
        date: isToday ? "Today" : `${dayNum} ${monthStr}`,
        fullDate: `${dayNum} ${monthStr} ${d.getFullYear()}`,
        score: pointScore,
        riskPct: pointScore === null ? null : Math.max(0, Number((100 - pointScore).toFixed(2))),
        scannedAt: h ? h.scannedAt : null,
        scannedThatDay: h ? h.scannedThatDay : false,
        pending: !isToday && historyLoading,
      });
    }
    return days;
  }, [score, trendGrid, trendHistory]);

  // 2. Dynamic Y-Axis Scale
  const maxRiskInTimeline = useMemo(() => {
    return Math.max(...timeline.map((p) => (p.riskPct === null ? 0 : p.riskPct)), 0.1);
  }, [timeline]);

  const maxYAxis = useMemo(() => {
    if (maxRiskInTimeline > 50) return 100;
    if (maxRiskInTimeline > 25) return 50;
    if (maxRiskInTimeline > 10) return 25;
    if (maxRiskInTimeline > 4) return 10;
    return 5;
  }, [maxRiskInTimeline]);

  const yAxisSteps = useMemo(() => {
    const step = maxYAxis / 5;
    return [5, 4, 3, 2, 1, 0].map((i) => {
      const val = i * step;
      return `${val % 1 === 0 ? val : val.toFixed(1)}%`;
    });
  }, [maxYAxis]);

  // 3. Bar heights as a percentage of the Y-axis scale
  const trendBars = useMemo(() => {
    return timeline.map((pt) => ({
      ...pt,
      heightPct:
        pt.riskPct === null || pt.riskPct === undefined
          ? 0
          : Math.min(100, Math.max(0, (pt.riskPct / maxYAxis) * 100)),
    }));
  }, [timeline, maxYAxis]);

  // Background zones on the visible scale (bottom → top)
  const trendZones = [
    { key: "low", label: "Low", from: 0, to: 40 },
    { key: "medium", label: "Medium", from: 40, to: 80 },
    { key: "high", label: "High", from: 80, to: 100 },
  ];

  const trendStrokeColor =
    normRisk === "critical"
      ? "#ef4444"
      : normRisk === "high"
      ? "#f97316"
      : normRisk === "medium" || normRisk === "med"
      ? "#eab308"
      : normRisk === "low"
      ? "#22c55e"
      : "var(--brand-primary, #2f6fed)";

  return (
    <React.Fragment>
      {/* Top Header Row: If title is provided directly */}
      {title && (
        <div className="feature-header-row">
          <div className="feature-header-left">
            <div className="feature-breadcrumbs">
              <Link to="/dashboard">Dashboard</Link>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m9 18 6-6-6-6" />
              </svg>
              <span className="active">{breadcrumbItem || title}</span>
            </div>
            <div className="feature-title-row">
              <h1>{title}</h1>
              <div className="feature-site-pill">
                <span className="status-dot" />
                <span>{displayDomain}</span>
              </div>
            </div>
          </div>

          {onRefresh && (
            <button
              type="button"
              className="feature-btn-refresh"
              onClick={onRefresh}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" color="light" />
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <FeatherIcon icon="refresh-cw" size={13} />
                  <span>Refresh</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Top KPI Grid Card: Score | Risk | Severities */}
      <div className="feature-kpi-grid">
        {/* Cell 1: Score */}
        <div className="feature-kpi-cell">
          <div className="feature-kpi-label">Score</div>
          <div className="feature-kpi-value-row">
            <span className="big-score">{score}</span>
            <span className="score-sub">/ 100</span>
          </div>
          <div className="feature-progress-track">
            <div
              className="progress-fill"
              style={{ width: `${Math.min(100, Math.max(0, Number(score) || 0))}%` }}
            />
          </div>
        </div>

        {/* Cell 2: Risk */}
        <div className="feature-kpi-cell">
          <div className="feature-kpi-label">Risk</div>
          <div className="feature-kpi-value-row">
            <span className="big-score" style={{ color: riskColor }}>
              {currentRisk}
            </span>
          </div>
          <div className="feature-level-bars">
            {levelSegments.map((seg, idx) => (
              <div
                key={idx}
                className="level-segment"
                style={{ background: seg.color, opacity: seg.opacity }}
              />
            ))}
          </div>
        </div>

        {/* Cell 3: Severity 5-cards Grid */}
        <div className="feature-kpi-cell">
          <div className="feature-kpi-label">Severity</div>
          <div className="feature-severity-grid">
            {severitiesList.map((s) => (
              <div
                key={s.key}
                className={`severity-mini-card ${s.key}`}
                style={s.color ? { borderTopColor: s.color } : {}}
              >
                <span className="num" style={s.numColor ? { color: s.numColor } : {}}>
                  {s.count}
                </span>
                <span className="label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Middle Card: Risk Trend */}
      {showTrend && (
        <div className="feature-trend-card">
          <div className="feature-card-header">
            <div className="trend-header-left">
              <span>Risk trend</span>
              <span className={`trend-pill ${normRisk}`}>
                <span className="dot" /> {currentRisk} Exposure ({currentExposure}% Risk)
              </span>
            </div>
            <span className="trend-meta-hint">Timeline: Last 6 Days</span>
          </div>
          <div className="feature-trend-chart-box" style={{ "--trend-color": trendStrokeColor }}>
            <div className="trend-y-axis">
              {yAxisSteps.map((stepLabel, idx) => (
                <span key={idx} style={{ top: `${(idx / (yAxisSteps.length - 1)) * 100}%` }}>
                  {stepLabel}
                </span>
              ))}
            </div>
            <div className="trend-plot-area">
              {/* Severity zones */}
              {trendZones.map((z) => (
                <div
                  key={z.key}
                  className={`trend-zone ${z.key}`}
                  style={{ bottom: `${z.from}%`, height: `${z.to - z.from}%` }}
                />
              ))}
              <div className="grid-line baseline" />

              {/* Bars */}
              <div className="trend-bars">
                {trendBars.map((bar, idx) => {
                  const isLast = idx === trendBars.length - 1;
                  const align = idx === 0 ? "start" : isLast ? "end" : "center";
                  const noData = bar.riskPct === null || bar.riskPct === undefined;
                  const colClass = [
                    "trend-bar-col",
                    isLast ? "is-current" : "",
                    noData ? "is-empty" : "",
                    bar.pending ? "is-pending" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <div key={idx} className={colClass}>
                      <div className="trend-bar-stack" style={{ height: `${bar.heightPct}%` }}>
                        <span className="trend-bar-value">
                          {bar.pending ? "…" : noData ? "No scan" : `${bar.riskPct}%`}
                        </span>
                        <div className="trend-bar" style={{ animationDelay: `${idx * 60}ms` }} />
                        {!bar.pending && (
                          <div className={`trend-tooltip align-${align}`}>
                            <div className="tt-date">{bar.fullDate}</div>
                            {noData ? (
                              <div className="tt-row muted">
                                <span>No scan had run yet</span>
                              </div>
                            ) : (
                              <>
                                <div className="tt-row">
                                  <span className="tt-swatch" />
                                  <span>Risk exposure</span>
                                  <strong>{bar.riskPct}%</strong>
                                </div>
                                <div className="tt-row muted">
                                  <span>Security score</span>
                                  <strong>{bar.score}/100</strong>
                                </div>
                                {bar.scannedAt && (
                                  <div className="tt-row muted tt-note">
                                    <span>
                                      {bar.scannedThatDay ? "Scanned" : "No new scan · last scan"}
                                    </span>
                                    <strong>{formatScanTime(bar.scannedAt)}</strong>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="trend-zone-labels">
              {trendZones.map((z) => (
                <span
                  key={z.key}
                  className={z.key}
                  style={{ bottom: `${(z.from + z.to) / 2}%` }}
                >
                  {z.label}
                </span>
              ))}
            </div>

            <div />
            <div className="trend-x-axis">
              {trendBars.map((bar, idx) => (
                <span key={idx} className={idx === trendBars.length - 1 ? "is-current" : ""}>
                  {bar.date}
                </span>
              ))}
            </div>
            <div />
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default FeatureKpiHeader;
